

import { useEffect, useRef } from "react";

const openModalStack: symbol[] = [];

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
  footer?: React.ReactNode;
}

const sizeClasses = {
  sm: "sm:max-w-md",
  md: "sm:max-w-lg",
  lg: "sm:max-w-2xl",
  xl: "sm:max-w-4xl",
};

export default function Modal({ isOpen, onClose, title, children, size = "md", footer }: ModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Escape closes only the topmost open modal, so a stacked picker doesn't also close the form under it
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (!isOpen) return;
    const id = Symbol("modal");
    openModalStack.push(id);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && openModalStack[openModalStack.length - 1] === id) onCloseRef.current();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      openModalStack.splice(openModalStack.indexOf(id), 1);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex sm:items-center sm:justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-stone-900/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal - Fullscreen on mobile, centered on desktop */}
      <div className={`relative bg-white w-full h-full sm:h-auto sm:rounded-2xl sm:border sm:border-stone-200 ${sizeClasses[size]} sm:max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-4 sm:zoom-in-95 duration-200`}>
        {/* Header - Fixed at top */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-stone-200 bg-white flex-shrink-0">
          <h2 className="text-lg font-bold text-stone-800 font-serif">{title}</h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="w-10 h-10 sm:w-8 sm:h-8 rounded-xl bg-stone-100 text-stone-500 hover:bg-stone-200 hover:text-stone-700 flex items-center justify-center transition-colors flex-shrink-0"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {children}
        </div>

        {/* Footer - Fixed at bottom (optional) */}
        {footer && (
          <div className="border-t border-stone-200 px-4 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:px-6 sm:pb-4 bg-white flex-shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
